import { ERestoreEntityType } from '@/enum';
import { ICreateLessonPayload, IAddLessonGamePayload } from '@/types/payload';
import { formatQuery } from '@/utils';

import http from './http';
import serviceHelper from './serviceHelper';

/**
 * Get list of lessons with optional filters
 * @param query - Query parameters for filtering
 * @returns Promise with lessons list
 */
export const getLessonList = async (query?: Record<string, unknown>) => {
  const queryString = query ? formatQuery(query) : '';
  return serviceHelper(http.get(`/school/lesson?${queryString}`), false);
};

/**
 * Get single lesson details by ID
 * @param id - Lesson ID
 * @returns Promise with lesson details
 */
export const getLessonDetails = async (id: string) => {
  return serviceHelper(http.get(`/school/lesson/${id}`), false);
};

/**
 * Create a new lesson
 * @param data - Lesson data
 * @returns Promise with created lesson
 */
export const createLesson = async (data: ICreateLessonPayload) => {
  return serviceHelper(http.post('/lesson', data), true);
};

/**
 * Update lesson by ID
 * @param id - Lesson ID
 * @param data - Updated lesson data
 * @returns Promise with updated lesson
 */
export const updateLesson = async (
  id: string,
  data: Record<string, unknown>
) => {
  return serviceHelper(http.put(`/school/lesson/${id}`, data), true);
};

/**
 * Delete lesson by ID
 * @param id - Lesson ID
 * @param recordType - Record type for deletion
 * @returns Promise with deletion result
 */
export const removeLesson = async (
  id: string,
  recordType: ERestoreEntityType
) => {
  return serviceHelper(
    http.delete(`/school/restore/${id}?record_type=${recordType}`),
    true
  );
};

/**
 * Assign lesson to class
 * @param lessonId - Lesson ID
 * @param classId - Class ID
 * @returns Promise with assignment result
 */
export const assignLessonToClass = async (
  lessonId: string,
  classId: string
) => {
  return serviceHelper(
    http.post('/school/lesson/assign', { lessonId, classId }),
    true
  );
};

export const getLessonTeacherDropdown = async (
  classIds: string[],
  exclude_block?: boolean
) => {
  const queryString = formatQuery({ class_id: classIds, exclude_block });
  return serviceHelper(
    http.get(`/school/lesson/teacher/dropdown?${queryString}`),
    false
  );
};

export const getLessonStudentDropdown = async (
  classIds: string[],
  exclude_block?: boolean,
  lesson_id?: string[]
) => {
  const queryString = formatQuery({
    class_id: classIds,
    exclude_block,
    lesson_id,
  });
  return serviceHelper(
    http.get(`/school/lesson/student/dropdown?${queryString}`),
    false
  );
};

//school portal lesson module services
export const getSchoolLessonBreakdown = async (id: string) => {
  return serviceHelper(http.get(`/school/lesson/${id}/breakdown`), false);
};

export const getSchoolLessonStudents = async (
  id: string,
  query?: Record<string, unknown>
) => {
  const queryString = query ? formatQuery(query) : '';
  return serviceHelper(
    http.get(`/school/lesson/${id}/students?${queryString}`),
    false
  );
};

export const removeSchoolLessonBreakdownGame = async (
  lessonId: string,
  gameId: string
) => {
  return serviceHelper(
    http.delete(`/school/lesson/${lessonId}/game/${gameId}`),
    true
  );
};

export const getSchoolStudentLessonDetails = async (
  lessonId: string,
  studentId: string
) => {
  return serviceHelper(
    http.get(`/school/lesson/${lessonId}/students/${studentId}`),
    false
  );
};

//teacher portal services
export const getTeacherLessonList = async (query?: Record<string, unknown>) => {
  const queryString = query ? formatQuery(query) : '';
  return serviceHelper(http.get(`/teacher/lesson?${queryString}`), false);
};

export const getTeacherLessonSummary = async () => {
  return serviceHelper(http.get('/teacher/lesson/summary'), false);
};

export const blockTeacherLesson = async (id: string, is_active: string) => {
  return serviceHelper(
    http.patch(`/teacher/lesson/${id}/block?is_active=${is_active}`),
    true
  );
};

export const removeTeacherLesson = async (id: string) => {
  return serviceHelper(http.delete(`/teacher/lesson/${id}`), true);
};

export const createTeacherLesson = async (data: ICreateLessonPayload) => {
  return serviceHelper(http.post('/teacher/lesson', data), true);
};

export const getTeacherStudentsDropdown = async (queryString: string) => {
  return serviceHelper(
    http.get(`/teacher/students/dropdown?${queryString}`),
    false
  );
};

export const getTeacherLessonDetails = async (id: string) => {
  return serviceHelper(http.get(`/teacher/lesson/${id}`), false);
};

export const getTeacherLessonBreakdown = async (id: string) => {
  return serviceHelper(http.get(`/teacher/lesson/${id}/breakdown`), false);
};

//for getting teacher student list for specific lesson
export const getTeacherLessonStudents = async (
  id: string,
  query?: Record<string, unknown>
) => {
  const queryString = query ? formatQuery(query) : '';
  return serviceHelper(
    http.get(`/teacher/lesson/${id}/students?${queryString}`),
    false
  );
};

//for getting student details for specific lesson in teacher portal
export const getTeacherStudentLessonDetails = async (
  lessonId: string,
  studentId: string
) => {
  return serviceHelper(
    http.get(`/teacher/lesson/${lessonId}/students/${studentId}`),
    false
  );
};

export const getTeacherLessonDropdown = async (queryString: string) => {
  return serviceHelper(
    http.get(`/teacher/lesson/dropdown?${queryString}`),
    false
  );
};

export const removeTeacherLessonBreakdownGame = async (
  lessonId: string,
  gameId: string
) => {
  return serviceHelper(
    http.delete(`/teacher/class/lesson/${lessonId}/game/${gameId}`),
    true
  );
};

export const getTeacherClassLessonDetails = async (
  classId: string,
  lessonId: string
) => {
  return serviceHelper(
    http.get(`/teacher/class/${classId}/lesson/${lessonId}`),
    false
  );
};

export const removeTeacherClassLesson = async (
  classId: string,
  lessonId: string
) => {
  return serviceHelper(
    http.delete(`/teacher/class/${classId}/lesson/${lessonId}`),
    true
  );
};

//for teacher class lesson breakdowns
export const getTeacherClassLessonAvailableGames = async (lessonId: string) => {
  return serviceHelper(
    http.get(`/teacher/class/lesson/${lessonId}/available-games`),
    false
  );
};

export const addLessonGame = async (
  lessonId: string,
  data: IAddLessonGamePayload
) => {
  return serviceHelper(http.post(`/lesson/${lessonId}/game`, data), true);
};

export const getTeacherClassLessonBreakdown = async (id: string) => {
  return serviceHelper(
    http.get(`/teacher/class/lesson/${id}/breakdown`),
    false
  );
};

export const getSchoolClassLessonBreakdownList = async (
  classId: string,
  query?: Record<string, unknown>
) => {
  const queryString = query ? formatQuery(query) : '';
  return serviceHelper(
    http.get(`/school/see-more/lesson-list/${classId}?${queryString}`),
    false
  );
};
